import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { ExtremaParams } from './extrema.model';

/**
 * Creates extrema points (maxima and minima) for functions.
 *
 * Subparts:
 * - `maxima`: Points at local maxima (sine peaks)
 * - `minima`: Points at local minima (sine troughs)
 * - `vertex`: The vertex point (quadratic only) - named 'max' or 'min' based on coefficient
 */
export class ExtremaCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'extrema';

  private readonly extremaCount = 3;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as ExtremaParams | undefined;
    if (!params?.of) {
      throw new Error('Extrema feature requires params.of to specify the parent function');
    }
    const parentFn: FunctionElement = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    if (parentFn.type === 'sine') {
      for (let n = -this.extremaCount; n <= this.extremaCount; n++) {
        objects.push(...this.createSinePoint(context, display, ['maxima'], parentFn, n, 'maxima'));
        objects.push(...this.createSinePoint(context, display, ['minima'], parentFn, n, 'minima'));
      }
    } else if (parentFn.type === 'cosine') {
      for (let n = -this.extremaCount; n <= this.extremaCount; n++) {
        objects.push(...this.createCosinePoint(context, display, ['maxima'], parentFn, n, 'maxima'));
        objects.push(...this.createCosinePoint(context, display, ['minima'], parentFn, n, 'minima'));
      }
    } else if (parentFn.type === 'quadratic') {
      // For quadratic, vertex is a min when a > 0, max when a < 0
      const isMax = () => parentFn.getParams()['a'] < 0;
      const style = resolveFeatureStyle('point', this.featureType, context, display, ['vertex']);
      const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['vertex']);
      // Use 'max' or 'min' based on the coefficient - can be overridden via style.label
      const name = isMax() ? 'max' : 'min';
      const result = elementFactory.point({
        coords: [() => parentFn.getParams()['h'], () => parentFn.getParams()['k']],
        name,
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    return {
      id,
      type: 'extrema',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }

  private createSinePoint(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[],
    parentFn: FunctionElement,
    n: number,
    type: 'maxima' | 'minima',
  ): GeometryElement[] {
    const { elementFactory, featureTheme } = context;
    const style = resolveFeatureStyle('point', this.featureType, context, display, path);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, path);

    const name = type === 'maxima' ? 'max' : 'min';

    // Sine: a * sin(b(x - c)) + d
    // Maxima at b(x - c) = π/2 + 2πn → x = c + π/(2b) + 2πn/b
    // Minima at b(x - c) = 3π/2 + 2πn → x = c + 3π/(2b) + 2πn/b
    const coords: [() => number, () => number] =
      type === 'maxima'
        ? [
            () => {
              const { b, c } = parentFn.getParams();
              return c + Math.PI / (2 * b) + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d + a;
            },
          ]
        : [
            () => {
              const { b, c } = parentFn.getParams();
              return c + (3 * Math.PI) / (2 * b) + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d - a;
            },
          ];

    const result = elementFactory.point({ coords, name, style: { ...style, visible } });
    const objects: GeometryElement[] = [result.point];
    if (result.label) {
      objects.push(result.label);
    }
    return objects;
  }

  private createCosinePoint(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[],
    parentFn: FunctionElement,
    n: number,
    type: 'maxima' | 'minima',
  ): GeometryElement[] {
    const { elementFactory, featureTheme } = context;
    const style = resolveFeatureStyle('point', this.featureType, context, display, path);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, path);

    const name = type === 'maxima' ? 'max' : 'min';

    // Cosine: a * cos(b(x - c)) + d
    // Maxima at b(x - c) = 2πn → x = c + 2πn/b
    // Minima at b(x - c) = π + 2πn → x = c + π/b + 2πn/b
    const coords: [() => number, () => number] =
      type === 'maxima'
        ? [
            () => {
              const { b, c } = parentFn.getParams();
              return c + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d + a;
            },
          ]
        : [
            () => {
              const { b, c } = parentFn.getParams();
              return c + Math.PI / b + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d - a;
            },
          ];

    const result = elementFactory.point({ coords, name, style: { ...style, visible } });
    const objects: GeometryElement[] = [result.point];
    if (result.label) {
      objects.push(result.label);
    }
    return objects;
  }
}
